import { computed, ref } from 'vue'
import { format } from 'date-fns'

import { deleteScheduledRedirectEvent, getScheduledEvents, scheduleEventsBatch } from '@/api/scheduledEvents'

import type { LeafletTabData, ScheduledLongLink, ScheduledThumbnailLink } from '../domain/leaflet-form.types'

export type ScheduledLongLinkEventPayload =
  | {
      type: 'UPDATE_LEAFLET_LONG_URL'
      contextId: string
      rootContextId: string
      context: 'LEAFLET'
      plannedTime: string
      longUrls: string[]
      devices: string[]
    }
  | {
      type: 'UPDATE_LEAFLET_LONG_URL'
      contextId: string
      rootContextId: string
      context: 'LEAFLET'
      plannedTime: string
      longURL: string | null
      devices: string[]
    }

export interface ScheduledThumbnailLinkEventPayload {
  type: 'UPDATE_LEAFLET_THUMBNAIL_URL'
  contextId: string
  rootContextId: string
  context: 'LEAFLET'
  plannedTime: string
  thumbnailURL?: string | null
  devices: string[]
}
export type ScheduledEventPayload = ScheduledLongLinkEventPayload | ScheduledThumbnailLinkEventPayload

interface LoadedScheduledEvents {
  scheduledLongLink: ScheduledLongLink | null
  scheduledThumbnailTrackingLink: ScheduledThumbnailLink | null
}

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

// Scheduled-event payloads can arrive in both legacy and current API shapes while
// environments roll forward, so the edit dialog must accept either representation.
function normalizeScheduledLongUrls(event: ScheduledLongLinkEventPayload): string[] {
  if ('longUrls' in event) {
    const normalizedLongUrls = normalizeStringArray(event.longUrls)

    if (normalizedLongUrls.length > 0) {
      return normalizedLongUrls
    }
  }

  return 'longURL' in event && typeof event.longURL === 'string' && event.longURL.length > 0 ? [event.longURL] : []
}

function normalizeScheduledThumbnailUrl(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function useScheduledEvents() {
  const scheduledLongLinkTouched = ref(false)
  const scheduledThumbnailTrackingLinkTouched = ref(false)
  const scheduledLongLinkContextId = ref<string | null>(null)
  const scheduledThumbnailTrackingLinkContextId = ref<string | null>(null)

  const hasScheduledEventsToProcess = computed(
    () => scheduledLongLinkTouched.value || scheduledThumbnailTrackingLinkTouched.value
  )

  function createScheduledLongLinkPayload(
    scheduledLink: ScheduledLongLink,
    leafletId: string
  ): ScheduledLongLinkEventPayload | null {
    if (!scheduledLink.date) {
      return null
    }

    const plannedTime = new Date(`${scheduledLink.date} ${scheduledLink.time}`)

    return {
      type: 'UPDATE_LEAFLET_LONG_URL',
      contextId: leafletId,
      rootContextId: leafletId,
      context: 'LEAFLET',
      plannedTime: plannedTime.toISOString(),
      longUrls: scheduledLink.longUrls,
      devices: scheduledLink.devices
    }
  }

  function createScheduledThumbnailLinkPayload(
    scheduledLink: ScheduledThumbnailLink,
    leafletId: string
  ): ScheduledThumbnailLinkEventPayload | null {
    if (!scheduledLink.date) {
      return null
    }

    const plannedTime = new Date(`${scheduledLink.date} ${scheduledLink.time}`)

    return {
      type: 'UPDATE_LEAFLET_THUMBNAIL_URL',
      contextId: leafletId,
      rootContextId: leafletId,
      context: 'LEAFLET',
      plannedTime: plannedTime.toISOString(),
      thumbnailURL: scheduledLink.thumbnailURL,
      devices: scheduledLink.devices
    }
  }

  async function deleteScheduledLongLink(): Promise<boolean> {
    if (!scheduledLongLinkContextId.value) {
      return false
    }

    try {
      const { status } = await deleteScheduledRedirectEvent('UPDATE_LEAFLET_LONG_URL', scheduledLongLinkContextId.value)
      return status === 200
    } catch {
      return false
    }
  }

  async function deleteScheduledThumbnailLink(): Promise<boolean> {
    if (!scheduledThumbnailTrackingLinkContextId.value) {
      return false
    }

    try {
      const { status } = await deleteScheduledRedirectEvent(
        'UPDATE_LEAFLET_THUMBNAIL_URL',
        scheduledThumbnailTrackingLinkContextId.value
      )
      return status === 200
    } catch {
      return false
    }
  }

  async function loadScheduledEventsForLeaflet(leafletId: string): Promise<LoadedScheduledEvents> {
    const result: LoadedScheduledEvents = {
      scheduledLongLink: null,
      scheduledThumbnailTrackingLink: null
    }

    try {
      const { data: scheduledEvents }: { data: ScheduledEventPayload[] } = await getScheduledEvents(leafletId)

      const longLinkEvent = scheduledEvents.find(event => event.type === 'UPDATE_LEAFLET_LONG_URL')

      const thumbnailEvent = scheduledEvents.find(event => event.type === 'UPDATE_LEAFLET_THUMBNAIL_URL')

      if (longLinkEvent) {
        scheduledLongLinkContextId.value = longLinkEvent.contextId
        result.scheduledLongLink = {
          date: format(new Date(longLinkEvent.plannedTime), 'yyyy-MM-dd'),
          time: format(new Date(longLinkEvent.plannedTime), 'HH:mm'),
          longUrls: normalizeScheduledLongUrls(longLinkEvent),
          devices: normalizeStringArray(longLinkEvent.devices)
        }
      } else {
        scheduledLongLinkContextId.value = null
      }

      if (thumbnailEvent) {
        scheduledThumbnailTrackingLinkContextId.value = thumbnailEvent.contextId
        result.scheduledThumbnailTrackingLink = {
          date: format(new Date(thumbnailEvent.plannedTime), 'yyyy-MM-dd'),
          time: format(new Date(thumbnailEvent.plannedTime), 'HH:mm'),
          thumbnailURL: normalizeScheduledThumbnailUrl(thumbnailEvent.thumbnailURL),
          devices: normalizeStringArray(thumbnailEvent.devices)
        }
      } else {
        scheduledThumbnailTrackingLinkContextId.value = null
      }
    } catch {
      scheduledLongLinkContextId.value = null
      scheduledThumbnailTrackingLinkContextId.value = null
    }

    return result
  }

  async function sendScheduledEventsForCreatedLeaflets(
    leaflets: LeafletTabData[],
    createdLeafletIds: string[]
  ): Promise<boolean> {
    const scheduledEvents: ScheduledEventPayload[] = []

    leaflets.forEach((leaflet, index) => {
      const leafletId = createdLeafletIds[index]

      const longLinkPayload = createScheduledLongLinkPayload(leaflet.scheduledLongLink, leafletId)
      if (longLinkPayload) {
        scheduledEvents.push(longLinkPayload)
      }

      const thumbnailPayload = createScheduledThumbnailLinkPayload(leaflet.scheduledThumbnailTrackingLink, leafletId)
      if (thumbnailPayload) {
        scheduledEvents.push(thumbnailPayload)
      }
    })

    if (scheduledEvents.length === 0) {
      return false
    }

    try {
      const { status } = await scheduleEventsBatch(scheduledEvents)
      return status === 200
    } catch {
      return false
    }
  }

  function resetScheduledEventsState(): void {
    scheduledLongLinkTouched.value = false
    scheduledThumbnailTrackingLinkTouched.value = false
    scheduledLongLinkContextId.value = null
    scheduledThumbnailTrackingLinkContextId.value = null
  }

  function touchScheduledLongLink(): void {
    scheduledLongLinkTouched.value = true
  }

  function touchScheduledThumbnailLink(): void {
    scheduledThumbnailTrackingLinkTouched.value = true
  }

  return {
    scheduledLongLinkTouched,
    scheduledThumbnailTrackingLinkTouched,
    scheduledLongLinkContextId,
    scheduledThumbnailTrackingLinkContextId,
    hasScheduledEventsToProcess,
    createScheduledLongLinkPayload,
    createScheduledThumbnailLinkPayload,
    deleteScheduledLongLink,
    deleteScheduledThumbnailLink,
    loadScheduledEventsForLeaflet,
    sendScheduledEventsForCreatedLeaflets,
    resetScheduledEventsState,
    touchScheduledLongLink,
    touchScheduledThumbnailLink
  }
}

export type UseScheduledEventsReturn = ReturnType<typeof useScheduledEvents>
